/*
 * Действия журнала: боковая панель записи (2-2.3.4), быстрая форма (2-2.7.1)
 * и диалоги (2-2.9.2, 2-2.9.3, 2-2.10.3, перенос).
 *
 * Геометрия — из макета «Журнал - П0»: панель 400 px от полос состояния до низа окна,
 * форма 380 px, диалог 440 px. Ни одного цвета: только var() из styles/tokens.css
 * и journal-tokens.css.
 *
 * ПОЧЕМУ ПАНЕЛЬ НЕ ЗАКРЫВАЕТ СЕТКУ ЦЕЛИКОМ. Администратор смотрит в карточку и в день
 * одновременно: «перенести на 18:30» проверяется взглядом в соседнюю колонку, а не
 * закрытием панели. Поэтому панель занимает 400 px справа и оставляет сетку видимой,
 * а на телефоне — становится отдельным экраном, потому что 400 px там нет.
 */

/* ─────────────────────── боковая панель записи ─────────────────────── */

.panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-popover);
  display: flex;
  flex-direction: column;
  width: 400px;
  max-width: 100%;
  overflow: hidden;
  background: var(--bg-card);
  border-left: 1px solid var(--border-strong);
  box-shadow: var(--panel-shadow);
}

.panelHead {
  display: flex;
  flex: none;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4) 10px;
  border-bottom: 1px solid var(--border);
}

.panelHeadText {
  min-width: 0;
}

.panelTitle {
  margin: 0;
  overflow: hidden;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panelSubtitle {
  font-size: var(--font-size-md);
  line-height: 1.35;
  color: var(--text-secondary);
}

.panelBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  padding: var(--space-3) var(--space-4);
  overflow: auto;
  font-size: var(--font-size-md);
}

.panelFoot {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: var(--space-1) 6px;
  padding: 10px var(--space-4);
  background: var(--bg-workspace);
  border-top: 1px solid var(--border);
}

/* Опасные действия прижаты вправо и отделены: «Не пришёл» не должен стоять первым
   под пальцем там, где рядом «Клиент пришёл» (проверка §5.16). */
.footSpacer {
  flex: 1;
  min-width: var(--space-2);
}

/* ─────────────────────── общие части панели и диалогов ─────────────────────── */

.sectionLabel {
  margin-bottom: 6px;
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Закреплённая заметка о клиенте: её нельзя не прочитать до начала визита. */
.note {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-2) 10px;
  font-weight: var(--font-weight-medium);
  line-height: 1.45;
  color: var(--note-fg);
  background: var(--note-bg);
  border-radius: var(--radius-md);
}

.noteMark {
  flex: none;
  font-weight: 700;
}

.noteDanger {
  color: var(--note-danger-fg);
  background: var(--note-danger-bg);
}

.noteCalm {
  color: var(--note-calm-fg);
  background: var(--note-calm-bg);
}

.notePlain {
  color: var(--note-plain-fg);
  background: var(--note-plain-bg);
}

.clientRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.clientPhone {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
}

.clientStats {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.clientButtons {
  display: flex;
  flex: none;
  gap: 6px;
}

/* Ссылка «Карточка» выглядит кнопкой: она ведёт на другой экран, и это переход,
   а не действие с записью. */
.linkButton {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-brand);
  text-decoration: none;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: 7px;
}

.linkButton:hover {
  background: var(--bg-workspace);
}

.items {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.itemRow {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2) 10px;
  border-bottom: 1px solid var(--grid-line-minor);
}

.itemRow:last-child {
  border-bottom: none;
}

.itemName {
  font-weight: var(--font-weight-semibold);
}

.itemMeta {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.itemPrice {
  font-variant-numeric: tabular-nums;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

.totalRow {
  background: var(--bg-workspace);
}

.totalLabel {
  color: var(--text-secondary);
}

.history {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: var(--font-size-md);
  color: var(--text-brand);
}

.historyVersion {
  color: var(--text-secondary);
}

/* Пробел в истории записи назван словами, а не спрятан: событийного журнала API
   не отдаёт, и делать вид, что в записи ничего не происходило, нечестно. */
.historyGap {
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--text-faint);
}

.comment {
  width: 100%;
  min-height: 52px;
  padding: 7px 10px;
  font: inherit;
  font-size: var(--font-size-md);
  color: var(--text-primary);
  resize: vertical;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
}

.comment:focus-visible {
  border-color: var(--accent);
  outline: none;
  box-shadow: var(--field-focus-ring);
}

.comment[readonly] {
  color: var(--text-secondary);
  background: var(--bg-workspace);
}

/* Отказ, который человек обязан прочитать: он остаётся на экране до следующего шага. */
.failure {
  padding: var(--space-2) 10px;
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--note-danger-fg);
  background: var(--note-danger-bg);
  border-radius: var(--radius-md);
}

/* ─────────────────────── быстрая форма 2-2.7.1 ─────────────────────── */

.form {
  position: absolute;
  z-index: var(--z-popover);
  display: flex;
  flex-direction: column;
  width: 380px;
  max-width: calc(100% - var(--space-4));
  font-size: var(--font-size-md);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-pop);
}

.formHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
}

.formTitle {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

.formWhere {
  min-width: 0;
  overflow: hidden;
  color: var(--text-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Отсчёт удержания. Цифры моноширинные: прыгающая ширина секунд дёргает всю шапку. */
/*
 * Истёкшее удержание в форме: объяснение и «Обновить» (2-2.4.1). Плашка внимания,
 * а не ошибки: слот, возможно, всё ещё свободен, и пугать красным человека, который
 * просто говорил по телефону дольше пяти минут, не за что.
 */
.holdLost {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  margin: 0 var(--space-3) var(--space-2);
  padding: 8px 10px;
  font-size: var(--font-size-sm);
  line-height: 1.35;
  color: var(--note-fg);
  background: var(--note-bg);
  border-radius: var(--radius-sm);
}

.holdChip {
  flex: none;
  margin-left: auto;
  padding: 2px var(--space-2);
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--note-fg);
  background: var(--note-bg);
  border-radius: var(--radius-pill);
}

.formBody {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--space-3) 14px;
}

.formRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.formFoot {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  padding: 10px 14px;
  background: var(--bg-workspace);
  border-top: 1px solid var(--border);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

.escHint {
  margin-left: auto;
  font-size: var(--font-size-xs-plus);
  line-height: 1.3;
  color: var(--text-faint);
  text-align: right;
}

/* Найденный клиент: карточка под полем телефона. */
.foundCard {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: var(--space-1);
  padding: var(--space-2) 10px;
  background: var(--bg-workspace);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.foundAvatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  color: var(--text-brand);
  background: var(--bg-sunken);
  border-radius: 50%;
}

.foundName {
  font-weight: var(--font-weight-semibold);
}

.foundMeta {
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--text-secondary);
}

.foundAlarm {
  font-weight: var(--font-weight-semibold);
  color: var(--note-danger-fg);
}

/* Несколько карточек на один номер: выбор, а не автоподстановка первой. */
.options {
  margin-top: var(--space-1);
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.option {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  padding: 7px 10px;
  font: inherit;
  font-size: var(--font-size-md);
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--bg-card);
  border: none;
  border-bottom: 1px solid var(--grid-line-minor);
}

.option:last-child {
  border-bottom: none;
}

.option:hover,
.option[aria-pressed="true"] {
  background: var(--bg-workspace);
}

.optionMeta {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.hint {
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--text-secondary);
}

/* Два РАЗДЕЛЬНЫХ согласия. Рамка вокруг них — не украшение: она показывает, что это
   один юридический блок из двух разных документов. */
.consents {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.consentSplit {
  height: 1px;
  background: var(--grid-line-minor);
}

.consentAds {
  color: var(--text-secondary);
}

/* ─────────────────────── «Это время только что заняли» ─────────────────────── */

.conflict {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 14px 0;
  padding: 10px var(--space-3);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-left: 3px solid var(--note-fg);
  border-radius: var(--radius-md);
}

.conflictTitle {
  font-size: 13px;
  font-weight: var(--font-weight-semibold);
}

.conflictText {
  line-height: 1.45;
  color: var(--text-brand);
}

.conflictFailed {
  line-height: 1.45;
  color: var(--note-danger-fg);
}

.conflictRow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.conflictLabel {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Каркас на месте будущих кнопок: 64×26 — ровно размер кнопки со временем,
   чтобы блок не прыгнул, когда окна доедут. */
.slotSkeleton {
  width: 64px;
  height: 26px;
  background: var(--bg-sunken);
  border-radius: var(--radius-pill);
}

.conflictAside {
  margin-left: auto;
}

/*
 * «Позвонить» — главное действие карточки на телефоне (5-А3): номер уже открыт,
 * и звонок уходит одним касанием, без промежуточного экрана.
 */
.callLink {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 6px 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-on-accent);
  text-decoration: none;
  background: var(--accent);
  border-radius: var(--radius-sm);
}

/* ─────────────────────── диалоги ─────────────────────── */

.scrim {
  position: absolute;
  inset: 0;
  z-index: calc(var(--z-popover) + 10);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  background: var(--scrim);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 440px;
  max-width: 100%;
  max-height: 100%;
  padding: 18px var(--space-5);
  overflow: auto;
  font-size: var(--font-size-md);
  background: var(--bg-card);
  border-radius: 12px;
  box-shadow: var(--shadow-pop);
}

.dialogTitle {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

/* Объяснение в диалоге: почему клиент не отменил сам. Плашка внимания, не ошибки. */
/* «Дата…» — выбор произвольного дня переноса рядом с чипами «Сегодня» и «Завтра». */
/* Окна переноса по частям дня: «Утро», «День», «Вечер» — как о времени говорят вслух. */
.windowGroups {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.windowGroup {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.windowGroupTitle {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.dayPick {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  min-height: 34px;
  padding: 4px 10px;
  font-size: var(--font-size-sm);
  cursor: pointer;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
}

.dayInput {
  width: 130px;
  font: inherit;
  background: transparent;
  border: 0;
}

.dialogNote {
  padding: 8px 12px;
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--note-fg);
  background: var(--note-bg);
  border-radius: var(--radius-sm);
}

.dialogSub {
  color: var(--text-secondary);
  line-height: 1.45;
}

.dialogText {
  line-height: 1.5;
  color: var(--text-brand);
}

.dialogButtons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  justify-content: flex-end;
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.fieldLabel {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.required {
  color: var(--danger);
}

/* Плитка «было → стало» в конфликте версий: сравнение читается за один взгляд. */
.versionTile {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
  padding: 10px var(--space-3);
  font-variant-numeric: tabular-nums;
  background: var(--bg-workspace);
  border-radius: var(--radius-md);
}

.versionCaption {
  font-size: var(--font-size-xs-plus);
  color: var(--text-secondary);
}

.versionValue {
  font-weight: var(--font-weight-semibold);
}

.versionArrow {
  color: var(--text-secondary);
}

/* Предупреждения сервера (confirmation_required): текст пишет сервер — правила знает он. */
.warnings {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) 10px;
  line-height: 1.45;
  color: var(--note-fg);
  background: var(--note-bg);
  border-radius: var(--radius-md);
}

.warningsTitle {
  font-weight: var(--font-weight-semibold);
}

/* ─────────────────────── деградация платформы ─────────────────────── */

.worksStripe {
  display: flex;
  flex: none;
  gap: 10px;
  align-items: center;
  min-height: var(--stripe-height);
  padding: var(--space-1) var(--space-4);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--works-fg);
  background: var(--works-bg);
}

.worksStop {
  color: var(--works-stop-fg);
  background: var(--works-stop-bg);
}

.worksTitle {
  flex: none;
  font-weight: var(--font-weight-semibold);
}

.worksText {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.worksAction {
  flex: none;
  margin-left: auto;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
}

/* Заглушка maintenance: журнал недоступен целиком, но день под ней остаётся. */
.blocking {
  position: absolute;
  inset: 0;
  z-index: calc(var(--z-popover) + 5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: var(--bg-workspace);
}

.blockingBox {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  max-width: 440px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-brand);
  text-align: center;
}

.blockingTitle {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.blockingHint {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* ─────────────────────── телефон ─────────────────────── */

/*
 * На 360 панель и форма перестают быть слоями поверх дня и становятся экраном:
 * 400 px справа на телефоне не существует, а «сжатая панель» — это нечитаемые
 * две трети строки. Правило то же, что у сетки: телефон — не сжатый десктоп.
 */
@media (max-width: 560px) {
  .panel,
  .form {
    position: fixed;
    inset: 0;
    width: auto;
    max-width: none;
    border: none;
    border-radius: 0;
  }

  .formRow {
    grid-template-columns: 1fr;
  }

  /*
   * ДИАЛОГ НА ТЕЛЕФОНЕ — НИЖНИЙ ЛИСТ (5-А3). Окно по центру экрана на 360 требует
   * тянуться большим пальцем вверх и закрывает собой ту самую запись, о которой
   * спрашивает. Лист снизу открывается под рукой, а список причин отмены в нём
   * прокручивается, не уезжая за край.
   */
  .scrim {
    align-items: flex-end;
    padding: 0;
  }

  .dialog {
    width: 100%;
    max-height: 88%;
    padding-bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
    border-radius: 18px 18px 0 0;
  }

  /*
   * Цели для пальца. 44 px — нижняя граница попадания (WCAG 2.5.8 и ТЗ §2.13),
   * а действия карточки — то, что нажимают в спешке и не глядя.
   */
  .panelFoot button,
  .panelFoot a,
  .dialog button,
  .clientButtons a,
  .callLink {
    min-height: 44px;
  }
}

/* ── 5-А2: услуги списком и шаги на телефоне ──────────────────────────────── */

.serviceBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.serviceChips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

/* Кнопка, а не строка списка: на телефоне выбранное должно оставаться видимым,
   а не прятаться в системной шторке, которую ради второй услуги открывают заново. */
.serviceChip {
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-card);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.serviceChipOn {
  composes: serviceChip;
  border-color: var(--accent);
  background: var(--status-in-progress-bg);
  font-weight: var(--font-weight-semibold);
}

.serviceTotal {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.fieldHint {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Два действия в ряд: отказ от записи — такое же явное решение, как согласие. */
.warningsActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin-top: var(--space-1);
}

/* ── Шаги ─────────────────────────────────────────────────────────────────── */

.stepHead {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 var(--space-2) var(--space-1);
}

.stepName {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Точки шагов: залиты до текущего включительно — видно и где ты, и сколько осталось. */
.stepDots {
  display: flex;
  gap: 4px;
}

.stepDot {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: var(--border);
}

.stepDotOn {
  composes: stepDot;
  background: var(--accent);
}

/* Подвал шагов прижат к низу: большой палец держит телефон снизу, и обе кнопки
   обязаны быть в его досягаемости, а не под прокруткой. */
.stepFoot {
  display: flex;
  gap: var(--space-1);
  padding: var(--space-2);
  border-top: 1px solid var(--border);
  background: var(--bg-card);
  position: sticky;
  bottom: 0;
}

.stepBack {
  min-width: 96px;
}

.stepNext {
  flex: 1;
}

.summaryRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.summaryRow {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  align-items: baseline;
}

.summaryKey {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.summaryValue {
  font-weight: var(--font-weight-semibold);
  text-align: right;
}

.summaryNote {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Окна-кнопки шага «Время»: то, что можно продать, а не пустой календарь. */
.windowGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.windowChip {
  min-width: 72px;
  min-height: 48px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-card);
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.windowChipOn {
  composes: windowChip;
  border-color: var(--accent);
  background: var(--accent);
  color: var(--text-on-accent);
  font-weight: var(--font-weight-semibold);
}

.dayChips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

/* ── Прокрутка формы на телефоне ──────────────────────────────────────────
   Полноэкранная форма без прокрутки — это поля, до которых нельзя добраться:
   на 360×640 в шаг «клиент» не помещаются имя и два согласия, и выбрать их
   было физически нечем. Тело прокручивается, шапка и подвал остаются на месте. */
@media (max-width: 560px) {
  .form {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .formBody {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .stepFoot {
    flex-shrink: 0;
  }
}
